import { defineStore } from 'pinia';
import { ref, computed, nextTick } from 'vue';
import formatCurrency from '@nuxt-common/media-card-large/app/utils/currencyFormatter';

import { useMultiATBStore } from '~/stores/useMultiATBStore';
import type { MultiAddToBagRequestPayload } from '~/stores/useMultiATBStore';
export interface SelectedItem {
  productId: string;
  upcId: string;
  upcNumber: string;
  quantity: number;
  price: number;
  priceTypeId?: number | string;
  promoMessaging?: string;
  imageUrl: string;
  alt?: string;
  sellerId: number | null;
  isMarketPlaceEligibleFlag?: boolean;
}

/**
 * Store for managing selections in the Shop The Collection (STC) experience.
 * Handles item quantities, price calculations, and the payload for Add To Bag.
 */
export const useCollectionSelectionStore = defineStore('collectionSelection', () => {
  const selections = ref<Record<string, SelectedItem>>({});
  const isSilentMode = ref(false);

  /** Total quantity of all selected items */
  const totalQuantity = computed(() => Object.values(selections.value).reduce((sum, item) => sum + item.quantity, 0));

  /** Estimated total price of all selected items, rounded to 2 decimal places */
  const estimatedTotal = computed(() => {
    const sum = Object.values(selections.value).reduce((acc, item) => acc + item.price * item.quantity, 0);
    const roundedSum = Math.round((sum + Number.EPSILON) * 100) / 100;
    return formatCurrency(roundedSum);
  });

  /** Returns true if there are any items selected */
  const hasSelections = computed(() => totalQuantity.value > 0);

  /** Payload for the Add To Bag XAPI request (/xapi/bag/v2/add contract) */
  type BaseOptions = {
    isMarketPlaceEligibleFlag: boolean;
    loyaltyPoints: boolean;
  };

  const getAddToBagPayload = <T extends Record<string, unknown>>(options: T & BaseOptions = {} as T & BaseOptions): MultiAddToBagRequestPayload => {
    return {
      lineItems: Object.values(selections.value).map((item) => ({
        upcId: item.upcId,
        upcNumber: item.upcNumber,
        registryId: 0,
        source: 'PDPSTSA2B',
        quantity: item.quantity,
        pickUpFromStore: 'false',
        pdpAtbProtectionPlanEligible: false,
        sellerId: item.sellerId,
        autoreplenishment: null,
        loyaltyPoints: options.loyaltyPoints ?? true,
        isMarketPlaceEligibleFlag: options.isMarketPlaceEligibleFlag || false,
      })),
    };
  };

  const getSelectionKey = (productId: string | number, upcId: string | number) => `${productId}_${upcId}`;

  /** Get quantity for a specific UPC */
  const getQuantityForUpc = (productId: string | number, upcId: string | number) => {
    return selections.value[getSelectionKey(productId, upcId)]?.quantity || 0;
  };

  /** Get total selected quantity for all UPCs of a specific product */
  const getQuantityForProduct = (productId: string | number) => {
    const id = String(productId);
    return Object.values(selections.value)
      .filter((item) => item.productId === id)
      .reduce((sum, item) => sum + item.quantity, 0);
  };

  /** Update or remove a selection */
  const updateSelection = (productId: string | number, upcId: string | number, updates: Partial<Omit<SelectedItem, 'productId' | 'upcId'>>) => {
    const multiATBStore = useMultiATBStore();
    if (multiATBStore.isLoading) {
      return;
    }

    multiATBStore.resetState();

    const key = getSelectionKey(productId, upcId);
    const id = String(productId);
    const upc = String(upcId);

    const existingItem = selections.value[key] || {
      productId: id,
      upcId: upc,
      upcNumber: '',
      quantity: 0,
      price: 0,
      imageUrl: '',
      sellerId: null,
    };

    const mergedItem = { ...existingItem, ...updates };

    if (mergedItem.quantity <= 0) {
      const { [key]: _removed, ...newSelections } = selections.value;
      selections.value = newSelections;
      return;
    }

    selections.value[key] = mergedItem;
  };

  /**
   * Clears all selections.
   * @param silent - If true, sets 'isSilentMode' to suppress 'Item removed' toasts.
   */
  const clearSelection = (silent = false) => {
    const multiATBStore = useMultiATBStore();
    if (!silent && multiATBStore.isLoading) {
      return;
    }

    if (!silent) {
      multiATBStore.resetState();
    }

    if (silent) {
      isSilentMode.value = true;
      selections.value = {};
      nextTick(() => {
        isSilentMode.value = false;
      });
    } else {
      selections.value = {};
    }
  };

  return {
    selections,
    isSilentMode,
    totalQuantity,
    estimatedTotal,
    hasSelections,
    getAddToBagPayload,
    getQuantityForUpc,
    getQuantityForProduct,
    updateSelection,
    clearSelection,
  };
});
